<script setup lang="ts">
import { copyToClipboard } from '@/utils/apostatudo/clipboard';

const props = defineProps({
  userId: {
    type: Number,
    required: true,
  },
  userCurrency: {
    type: String,
    required: true,
  },
  userLanguage: {
    type: String,
    required: true,
  },
  userCountry: {
    type: String,
    required: true,
  },
  minDepositAmount: {
    type: Number,
    required: true,
  },
  maxDepositAmount: {
    type: Number,
    required: true,
  },
  activeFirstDepositMessage: {
    type: Boolean,
    default: false,
  },
  firstDepositMessage: {
    type: String,
    default: '',
  },
  utmCampaign: {
    type: String,
    default: '',
  },
  utmSource: {
    type: String,
    default: '',
  },
  utmMedium: {
    type: String,
    default: '',
  },
});

const isGetAddressLoading = ref<boolean>(false);

const showLoading = computed(() => {
  return isLoading.value || isGetAddressLoading.value;
});

const depositValues = ref({
  amount: 0,
});

const amountConverted = ref(0);

watch(
  () => depositValues.value.amount,
  () => {
    if (depositValues.value.amount) {
      const multiply = depositValues.value.amount / 62000.68;

      amountConverted.value = Number(multiply.toFixed(8));
    }
  },
);

const isAddressCopied = ref<boolean>(false);
const inputAddressElement = ref<HTMLInputElement | null>(null);

const copyCode = async () => {
  const codeToCopy = cryptoWallet.value?.address || '';

  try {
    inputAddressElement.value?.select();
  } catch {}

  if (await copyToClipboard(codeToCopy)) {
    isAddressCopied.value = true;
    setTimeout(() => (isAddressCopied.value = false), 2000);
  }
};

const isAddressQrCodeVisible = computed<boolean>(() => {
  return !!cryptoWallet.value?.qr_code;
});

const { cryptoList, isLoading, hasError: hasErrorFetching } = useCrypto();

const cryptoWallet = ref<{ address: string; qr_code: string }>();
const selectedCryptoAsset = ref<Crypto.Asset>();
const selectedNetwork = ref<Crypto.Network>();

const computedCryptoSymbol = computed<Crypto.Asset['name']>(() => {
  return selectedCryptoAsset.value?.name || 'XXX';
});

function formatCryptoAddress(address: string) {
  if (address.length < 12) {
    return address;
  }

  const firstPart = address.slice(0, 4);
  const lastPart = address.slice(-4);
  const middleStartIndex = Math.floor((address.length - 4) / 2);
  const middlePart = address.slice(middleStartIndex, middleStartIndex + 4);

  const formattedAddress = `<span>${firstPart}</span>${address.slice(4, middleStartIndex)}<span>${middlePart}</span>${address.slice(middleStartIndex + 4, -4)}<span>${lastPart}</span>`;

  return formattedAddress;
}

const formattedAddress = computed(() => {
  return formatCryptoAddress(cryptoWallet.value?.address || '');
});

const errorToGetAddress = ref<boolean>(false);

const isAllReady = computed(() => {
  return (
    !showLoading.value && !hasErrorFetching.value && !errorToGetAddress.value
  );
});

watch(selectedNetwork, async () => {
  if (!selectedNetwork.value) {
    return;
  }

  isGetAddressLoading.value = true;
  errorToGetAddress.value = false;

  const cryptoSymbol =
    isDev() || isStage() ? `ETH_TEST6` : selectedNetwork.value.id;

  const { ApiService } = useApiService();

  await ApiService.post('/crypto/wallet', {
    user_id: props.userId,
    crypto_symbol: cryptoSymbol,
  })
    .then((response) => {
      cryptoWallet.value = response.data.data;
    })
    .catch((error) => {
      errorToGetAddress.value = true;
      console.error(error);
    })
    .finally(() => {
      isGetAddressLoading.value = false;
    });
});
</script>

<template>
  <ClientOnly>
    <div class="w-full md:max-w-[450px] flex flex-col gap-2 mx-auto relative">
      <div
        v-if="hasErrorFetching && !showLoading"
        :class="$style.cryptoError"
      >
        <NuxtIcon
          :class="$style.icon"
          name="exclamation"
        />
        <span>
          {{ $t('deposit.crypto.errors.get_all_cryptos') }}
        </span>
      </div>

      <div :class="$style.depositSelects">
        <div
          v-if="showLoading"
          :class="$style.disableSelects"
        ></div>

        <div
          class="max-w-36"
          :class="$style.selectItem"
        >
          <label
            :class="$style.labelMoney"
            for="criptosymbol"
          >
            <span>{{ $t('deposit.crypto.deposit_currency') }}:</span>
          </label>
          <BaseFormCombobox
            v-model="selectedCryptoAsset"
            display-value="name"
            has-icon
            :options="cryptoList"
          >
            <template #icon="{ option }">
              <NuxtIcon
                :class="$style.criptosymbolIcon"
                :filled="true"
                :name="`cryptos/${option.name}`"
              />
            </template>
          </BaseFormCombobox>
        </div>

        <div :class="$style.selectItem">
          <label
            :class="$style.labelMoney"
            for="name"
          >
            <span>{{ $t('deposit.crypto.deposit_network') }}:</span>
          </label>

          <BaseFormCombobox
            v-model="selectedNetwork"
            class="flex-1"
            :disabled="!selectedCryptoAsset?.networks?.length"
            display-value="name"
            :options="selectedCryptoAsset?.networks"
          />
        </div>
      </div>

      <div :class="$style.cryptoResInfos">
        <BaseLoading
          class="!bg-bg-primary !absolute"
          :show="showLoading"
        />

        <div
          v-if="errorToGetAddress && !showLoading"
          :class="$style.errorAddress"
        >
          <NuxtIcon
            :class="$style.icon"
            name="exclamation"
          />
          <span>
            {{ $t('deposit.crypto.errors.get_address') }}
          </span>
        </div>

        <div :class="[$style.depositQrCode, { [$style.ready]: isAllReady }]">
          <img
            v-if="isAddressQrCodeVisible"
            id="qr-code-img"
            alt="Codigo QR PIX"
            fluid
            :src="cryptoWallet?.qr_code"
          />
        </div>

        <div
          :class="$style.clickToCopy"
          @click="copyCode"
        >
          <div :class="$style.handIcon">
            <NuxtIcon name="hand_pointer" />
          </div>

          <p :class="$style.addressLabel">
            <span>{{ $t('deposit.crypto.wallet_address') }}</span>
          </p>

          <div :class="$style.inputAddress">
            <div
              :class="$style.addressText"
              v-html="formattedAddress"
            >
            </div>
            <input
              ref="inputAddressElement"
              readonly
              type="text"
              :value="cryptoWallet?.address"
            />
            <button
              :class="$style.copyAddressButton"
              size="lg"
              variant="secondary"
            >
              <NuxtIcon
                v-if="isAddressCopied"
                name="check"
              />
              {{
                isAddressCopied
                  ? $t('deposit.crypto.code_copied')
                  : $t('deposit.crypto.copy_address')
              }}
            </button>
          </div>
        </div>
      </div>

      <div
        class="alert alert-success"
        :class="$style.alertCrypto"
        v-html="
          $t('deposit.crypto.deposit_alert', {
            crypto: computedCryptoSymbol,
          })
        "
      >
      </div>
    </div>
  </ClientOnly>
</template>

<style module lang="postcss">
.cryptoError {
  @apply absolute top-0 left-0 w-full h-full bg-bg-primary/90 flex items-center justify-center text-error text-sm text-center font-semibold z-10 px-4 gap-4;

  span {
    @apply text-left;
  }

  .icon {
    @apply text-6xl;
  }
}

.depositSelects {
  @apply flex flex-wrap gap-2 items-baseline justify-between relative;

  .disableSelects {
    @apply absolute top-0 left-0 w-full h-full bg-bg-primary/60 z-10;
  }

  .selectItem {
    @apply flex flex-col gap-2 flex-1;

    .labelMoney {
      @apply text-auth-texts text-xs font-semibold -mb-1;
    }

    .criptosymbolIcon {
      @apply w-3 h-3;
    }
  }
}

.errorAddress {
  @apply absolute top-0 left-0 w-full h-full bg-bg-primary/90 flex items-center justify-center text-error text-sm text-center font-semibold z-10 px-4 gap-4;

  span {
    @apply text-left;
  }

  .icon {
    @apply text-6xl;
  }
}

.alertCrypto {
  @apply w-full mt-2;

  span {
    @apply text-success font-bold;
  }

  strong {
    @apply underline;
  }
}

.cryptoResInfos {
  @apply flex justify-between items-center gap-3 mt-3 relative;

  .depositQrCode {
    @apply w-24 min-w-24 h-24 sm:w-28 sm:min-w-28 sm:h-28 bg-white/10 rounded-lg overflow-hidden relative;
    &.ready {
      @apply bg-white;
    }
    img {
      @apply w-full h-auto;
    }
  }

  .clickToCopy {
    @apply border-dashed border-auth-texts/60 border rounded-lg px-4 py-3 relative flex-1 min-h-28 flex items-start justify-center flex-col;

    .handIcon {
      @apply absolute top-3 right-3 text-auth-texts/40 text-xl cursor-pointer;
    }

    .addressLabel {
      @apply text-auth-texts/50 text-2xs sm:text-xs font-semibold leading-3 mb-1 sm:mb-2;
    }

    .inputAddress {
      @apply w-full flex items-center justify-between gap-2 relative;

      .addressText {
        @apply break-words break-all whitespace-break-spaces text-xs sm:text-sm font-semibold text-auth-texts pr-3;

        span {
          @apply text-primary;
        }
      }

      .copyAddressButton {
        @apply inline-flex items-center justify-center gap-2 text-auth-button-bg bg-auth-button-bg/10 py-1.5 px-3 rounded text-xs font-semibold;
      }

      input {
        @apply w-0 h-0 invisible absolute;
      }
    }
  }
}
</style>
